import { create } from "zustand"
import { persist } from "zustand/middleware"

export interface AuthUser {
  id: string
  email: string
  status?: string | null
  account_status?: string | null
  full_name?: string | null
  is_admin?: boolean
  email_verified?: boolean
  has_security_pin?: boolean
  security_pin_length?: 4 | 6 | null
  security_pin_reset_required?: boolean
  preferences?: {
    privacy_hide_balance?: boolean
    privacy_private_profile?: boolean
  }
  runtime_authority?: {
    membership?: {
      plan?: string
      groups?: string[]
      effectiveCapabilities?: string[]
      moduleVisibility?: Record<string, string>
      policyFlags?: Record<string, boolean>
      snapshotVersion?: string
      issuedAt?: string
      userBound?: string
      platform?: string
    }
  }
}

type AuthStatus =
  | "AUTH_LOADING"
  | "AUTH_LOGIN_IN_PROGRESS"
  | "AUTH_AUTHENTICATED"
  | "AUTH_REFRESHING"
  | "AUTH_UNAUTHENTICATED"
  | "AUTH_LOGGING_OUT"
  | "AUTH_LOGGED_OUT"

interface AuthState {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  status: AuthStatus
  bootstrap: () => Promise<void>
  login: (email: string, password: string, otp_code?: string) => Promise<void>
  register: (
    email: string,
    password: string,
    opts?: { invite_code?: string; referral_code?: string; security_phrase?: string }
  ) => Promise<{ status: string; email_delivery?: string }>
  logout: () => Promise<void>
  setUser: (user: AuthUser | null) => void
  refreshSession: () => Promise<boolean>
  refreshAuthority: () => Promise<boolean>
}

type MeResponse = {
  user?: Partial<AuthUser> & { id?: string | number; email?: string }
}

let loginInFlight: Promise<void> | null = null
let refreshInFlight: Promise<boolean> | null = null
let authorityRefreshInFlight: Promise<boolean> | null = null
let bootstrapInFlight: Promise<void> | null = null
let logoutInFlight: Promise<void> | null = null
let logoutCompletedSinceLoad = false  // PHASE 1: Terminal guard — never reset, survives page session
const AUTH_CHANNEL_NAME = "bpal-auth"
const isBrowser = () => typeof window !== "undefined"

function normalizeUser(raw: Partial<AuthUser> & { id?: string | number; email?: string } | null | undefined): AuthUser | null {
  if (!raw?.id || !raw?.email) return null
  return {
    id: String(raw.id),
    email: String(raw.email),
    status: raw.status ?? null,
    account_status: raw.account_status ?? null,
    full_name: raw.full_name ?? null,
    is_admin: raw.is_admin === true,
    email_verified: raw.email_verified === true,
    has_security_pin: raw.has_security_pin === true,
    security_pin_length:
      raw.security_pin_length === 4 || raw.security_pin_length === 6
        ? raw.security_pin_length
        : null,
    security_pin_reset_required: raw.security_pin_reset_required === true,
    preferences: raw.preferences,
    runtime_authority:
      raw.runtime_authority && typeof raw.runtime_authority === "object"
        ? (raw.runtime_authority as AuthUser["runtime_authority"])
        : undefined,
  }
}

async function fetchCurrentUser(): Promise<AuthUser | null> {
  const api = await getApiClient()
  const response = await api.get<MeResponse>("/me")
  return normalizeUser(response.data?.user)
}

async function getApiClient() {
  const mod = await import("@/lib/api")
  return mod.default
}

function broadcastAuthEvent(event: "login" | "logout") {
  if (!isBrowser()) return
  try {
    if ("BroadcastChannel" in window) {
      const channel = new BroadcastChannel(AUTH_CHANNEL_NAME)
      channel.postMessage({ event, timestamp: Date.now() })
      channel.close()
      return
    }
    window.localStorage.setItem(
      AUTH_CHANNEL_NAME,
      JSON.stringify({ event, timestamp: Date.now() })
    )
  } catch {
    // best effort only
  }
}

function applyAuthenticatedState(set: (patch: Partial<AuthState>) => void, user: AuthUser | null) {
  const nextStatus = user ? "AUTH_AUTHENTICATED" : "AUTH_UNAUTHENTICATED"
  const logoutCompleted = logoutCompletedSinceLoad

  // AUDIT: Log when applyAuthenticatedState is called
  // especially if trying to set AUTH_AUTHENTICATED after logout completed
  if (logoutCompleted && user) {
    console.warn('[auth.store] ⚠️ applyAuthenticatedState called with user AFTER logout completed:', {
      user: user?.id,
      logoutCompletedSinceLoad,
      nextStatus,
      stack: new Error().stack?.split('\n').slice(1, 4).join('\n')
    })
  } else if (nextStatus === "AUTH_AUTHENTICATED") {
    console.log('[auth.store] applyAuthenticatedState → AUTH_AUTHENTICATED', { user: user?.id })
  }

  set({
    user,
    isAuthenticated: Boolean(user),
    isLoading: false,
    status: nextStatus,
  })
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isLoading: true,
      isAuthenticated: false,
      status: "AUTH_LOADING",

      bootstrap: async () => {
        if (bootstrapInFlight) return bootstrapInFlight
        bootstrapInFlight = (async () => {
          set({ isLoading: true, status: "AUTH_LOADING" })
          try {
            const user = await fetchCurrentUser()
            applyAuthenticatedState(set, user)
          } catch {
            applyAuthenticatedState(set, null)
          } finally {
            bootstrapInFlight = null
          }
        })()
        return bootstrapInFlight
      },

      login: async (email: string, password: string, otp_code?: string) => {
        if (loginInFlight) return loginInFlight
        const task = (async () => {
          set({ isLoading: true, status: "AUTH_LOGIN_IN_PROGRESS" })
          try {
            const api = await getApiClient()
            await api.post(
              "/auth/login",
              { email, password, otp_code: otp_code || undefined },
              { withCredentials: true }
            )

            const user = await fetchCurrentUser()

            // Explicit login: reset post-logout terminal state so the session
            // authority can be re-validated for the new session. This is the only
            // path allowed to clear logoutCompletedSinceLoad — bootstrap() must not.
            if (logoutCompletedSinceLoad) {
              console.log('[auth.store] Explicit login after logout — resetting terminal state for new session')
              logoutCompletedSinceLoad = false
              const { useSessionAuthority } = await import('@/context/session.authority')
              useSessionAuthority.getState().setUnknown()
            }

            applyAuthenticatedState(set, user)
            broadcastAuthEvent("login")
          } catch (error) {
            applyAuthenticatedState(set, null)
            throw error
          }
        })()

        loginInFlight = task
        try {
          await task
        } finally {
          loginInFlight = null
        }
      },

      register: async (
        email: string,
        password: string,
        opts?: { invite_code?: string; referral_code?: string; security_phrase?: string }
      ) => {
        set({ isLoading: true, status: "AUTH_LOADING" })
        try {
          const api = await getApiClient()
          const response = await api.post(
            "/auth/register",
            {
              email,
              password,
              invite_code: opts?.invite_code || undefined,
              referral_code: opts?.referral_code || undefined,
              security_phrase: opts?.security_phrase || undefined,
            },
            { withCredentials: true }
          )

          const status = String(response.data?.status || "")
          const email_delivery = String(response.data?.email_delivery || "unknown")
          const user = await fetchCurrentUser().catch(() => null)
          applyAuthenticatedState(set, user)
          broadcastAuthEvent("login")
          return { status: status || "pending_approval", email_delivery }
        } catch (error) {
          applyAuthenticatedState(set, null)
          throw error
        }
      },

      logout: async () => {
        // PHASE 1: TERMINAL GUARD
        // If logout already completed this session, never repeat
        // This flag is NEVER reset — it survives the entire page session
        // Prevents: logout spam, repeated /auth/logout calls, logout recursion
        if (logoutCompletedSinceLoad) {
          console.log('[auth.store] Logout already completed this session — skipping')
          return
        }

        // IN-FLIGHT GUARD: Deduplicate concurrent calls
        // If logout already in progress, return the same promise
        if (logoutInFlight) {
          console.log('[auth.store] Logout already in progress — returning existing promise')
          return logoutInFlight
        }

        logoutInFlight = (async () => {
          const hadUser = Boolean(useAuthStore.getState().user)  // Capture BEFORE clearing

          // 1. Transition to LOGGING_OUT
          set({ isLoading: true, status: "AUTH_LOGGING_OUT" })
          console.log('[auth.store] Transitioning to AUTH_LOGGING_OUT')

          try {
            // 2. Backend call (best-effort)
            try {
              const api = await getApiClient()
              await api.post("/auth/logout", {}, { withCredentials: true })
              console.log('[auth.store] Backend /auth/logout succeeded')
            } catch (err) {
              console.warn('[auth.store] Backend logout endpoint failed (continuing):', err)
            }

            // 3. Clear local auth state
            applyAuthenticatedState(set, null)
            set({ status: "AUTH_LOGGED_OUT" })  // Override to terminal state
            console.log('[auth.store] Auth state cleared, status = AUTH_LOGGED_OUT')
            // Ensure persisted auth snapshot cannot resurrect stale authenticated state
            try {
              ;(useAuthStore as unknown as { persist?: { clearStorage?: () => void } })
                .persist
                ?.clearStorage
                ?.()
              console.log('[auth.store] Persisted auth storage cleared')
            } catch (persistError) {
              console.warn('[auth.store] Failed to clear persisted auth storage:', persistError)
            }

            // 4. Broadcast logout event to other tabs
            // PHASE 1 FIX: Only broadcast if there was actually a user to log out
            // Was: unconditional (could echo on empty state)
            if (hadUser) {
              console.log('[auth.store] Broadcasting logout event to other tabs')
              broadcastAuthEvent("logout")
            } else {
              console.log('[auth.store] No user to log out (already null), skipping broadcast')
            }

            // 5. Session invalidation pipeline
            try {
              const { SessionInvalidationHandler } = await import(
                "@/context/session-invalidation-handler"
              )
              console.log('[auth.store] Calling SessionInvalidationHandler.invalidate(user-logout)')
              await SessionInvalidationHandler.invalidate("user-logout")
            } catch (error) {
              console.error('[auth.store] Failed to invalidate session on logout:', error)
              // Continue anyway - we've already cleared auth state
            }
          } finally {
            // Set terminal flag — NEVER RESET
            logoutCompletedSinceLoad = true
            logoutInFlight = null
            console.log('[auth.store] Logout pipeline complete, terminal flag set')
          }
        })()

        return logoutInFlight
      },

      setUser: (user: AuthUser | null) => {
        applyAuthenticatedState(set, user)
      },

      refreshSession: async () => {
        if (refreshInFlight) return refreshInFlight

        refreshInFlight = (async () => {
          set({ status: "AUTH_REFRESHING", isLoading: true })
          try {
            const api = await getApiClient()
            await api.post("/auth/refresh", {}, { withCredentials: true })
            const user = await fetchCurrentUser()
            applyAuthenticatedState(set, user)
            return Boolean(user)
          } catch {
            applyAuthenticatedState(set, null)
            return false
          } finally {
            refreshInFlight = null
          }
        })()

        return refreshInFlight
      },

      refreshAuthority: async () => {
        // GUARD: Do not refresh authority after logout
        // logoutCompletedSinceLoad is a terminal flag that never resets during page session
        // This prevents: logout → async timer fires → refetch user → resurrect authenticated state
        if (logoutCompletedSinceLoad) {
          console.log('[auth.store] refreshAuthority() blocked: logout already completed this session')
          return false
        }

        if (authorityRefreshInFlight) return authorityRefreshInFlight

        authorityRefreshInFlight = (async () => {
          try {
            const user = await fetchCurrentUser()
            if (!user) return false
            const current = useAuthStore.getState().user
            const currentVersion = current?.runtime_authority?.membership?.snapshotVersion || ""
            const nextVersion = user.runtime_authority?.membership?.snapshotVersion || ""
            const currentIssuedAt = current?.runtime_authority?.membership?.issuedAt || ""
            const nextIssuedAt = user.runtime_authority?.membership?.issuedAt || ""
            if (currentVersion !== nextVersion || currentIssuedAt !== nextIssuedAt) {
              set({ user, isAuthenticated: true, isLoading: false, status: "AUTH_AUTHENTICATED" })
              return true
            }
            return false
          } catch {
            return false
          } finally {
            authorityRefreshInFlight = null
          }
        })()
        return authorityRefreshInFlight
      },
    }),
    {
      name: "auth-store",
      version: 2,
      migrate: () => ({}),
      partialize: () => ({}),
    }
  )
)
